import React from 'react';
import { motion } from 'framer-motion';
import { Icon as IconifyIcon } from '@iconify/react';
import { useI18n } from '../../lib/i18n';

const AwardsSection2 = () => {
  const { t } = useI18n()
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
    },
  };

  const barVariants = {
    hidden: { width: 0 },
    visible: (custom: number) => ({
      width: `${custom}%`,
      transition: { duration: 1.2, ease: "easeOut", delay: 0.5 },
    }),
  };

  return (
    <section className="bg-[#F4F5F7] py-16 md:py-24 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header Section */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-5xl font-semibold tracking-tight text-[#172B4D] mb-4"
          >
            {t.awards.title}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-lg text-[#172B4D]/70 leading-relaxed"
          >
            {t.awards.subtitle}
          </motion.p>
        </div>

        {/* Bento Grid */}
        <motion.div 
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          
          {/* Card A: Price Advantage (Col-Span-2) */}
          <motion.div 
            variants={itemVariants}
            whileHover={{ y: -4, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)" }}
            className="col-span-1 md:col-span-2 bg-white rounded-2xl p-8 md:p-10 border border-[#172B4D]/5 shadow-sm flex flex-col justify-between group"
          >
            <div className="mb-8">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-medium uppercase tracking-widest text-[#00BCC5]">{t.awards.savings}</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-[#172B4D]">{t.awards.priceAdvantage}</h3>
              <p className="text-[#172B4D]/60 mt-2">{t.awards.cheaperThanTerminal}</p>
            </div>

            <div className="space-y-6 w-full">
              {/* Comparison Item 1 */}
              <div>
                <div className="flex justify-between text-sm font-medium text-[#172B4D]/60 mb-2">
                  <span>{t.awards.terminalParking}</span>
                  <span>~ 140 €</span>
                </div>
                <div className="h-4 w-full bg-[#F4F5F7] rounded-full overflow-hidden">
                  <motion.div 
                    custom={90}
                    variants={barVariants}
                    className="h-full bg-[#172B4D]/20 rounded-full"
                  />
                </div>
              </div>

              {/* Comparison Item 2 */}
              <div>
                <div className="flex justify-between text-sm font-bold text-[#172B4D] mb-2">
                  <span className="flex items-center gap-2">
                    Parkbox Frankfurt
                    <IconifyIcon icon="ph:check-circle-fill" className="text-[#0052CC]" />
                  </span>
                  <span className="text-[#0052CC]">ab 99 €</span>
                </div>
                <div className="h-4 w-full bg-[#F4F5F7] rounded-full overflow-hidden relative">
                  <motion.div 
                    custom={45}
                    variants={barVariants}
                    className="h-full bg-[#0052CC] rounded-full relative"
                  >
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 w-2 h-2 bg-white rounded-full shadow-sm" />
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Card B: The Award (Col-Span-1) */}
          <motion.div 
            variants={itemVariants}
            whileHover={{ y: -4, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)" }}
            className="col-span-1 bg-white rounded-2xl overflow-hidden border border-[#172B4D]/5 shadow-sm relative flex flex-col"
          >
            <div className="p-8 pb-0 z-10">
              <h3 className="text-xl font-bold text-[#172B4D]">{t.awards.excellentService}</h3>
              <p className="text-[#172B4D]/60 text-sm mt-1">{t.awards.topRatings}</p>
            </div>
            <div className="flex-1 relative mt-6 flex items-center justify-center px-8 pb-8">
              <a
                href="https://www.parkplatzvergleich.de/parken-flughafen-frankfurt/park-box-frankfurt-1793.html#bewertungen"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-gradient-to-br from-[#F4F5F7] to-[#E8E9EB] rounded-2xl p-6 shadow-inner border border-white/50 cursor-pointer hover:shadow-md transition-shadow duration-300"
              >
                <img
                  src="/wertung2.png"
                  alt="Bewertung"
                  className="w-[200px] h-[200px] object-contain drop-shadow-lg transform hover:scale-105 transition-transform duration-500"
                />
              </a>
            </div>
            <div className="absolute bottom-6 left-8 z-20 flex items-center gap-2 bg-white/90 backdrop-blur-sm px-3 py-1.5 rounded-full border border-[#172B4D]/10 shadow-sm">
              <div className="flex -space-x-2">
                {[1,2,3].map(i => (
                  <div key={i} className="w-6 h-6 rounded-full bg-[#F4F5F7] border-2 border-white flex items-center justify-center text-[10px] text-[#172B4D] font-bold">
                    <IconifyIcon icon="ph:user-fill" className="text-[#172B4D]/40" />
                  </div>
                ))}
              </div>
              <span className="text-xs font-bold text-[#172B4D]">{t.awards.stars}</span>
            </div>
          </motion.div>

          {/* Card C: Starting Price (Col-Span-1) */}
          <motion.div 
            variants={itemVariants}
            whileHover={{ y: -4, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)" }}
            className="col-span-1 bg-white rounded-2xl p-8 border border-[#172B4D]/5 shadow-sm flex flex-col justify-center items-center text-center relative overflow-hidden"
          >
            <div className="absolute top-0 right-0 p-4">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-[#00BCC5]/10 text-[#00BCC5] tracking-wide uppercase">
                {t.awards.bestPriceGuarantee}
              </span>
            </div>
            
            <div className="mt-4">
              <p className="text-[#172B4D]/60 font-medium mb-2">{t.awards.startingFrom}</p>
              <div className="flex items-start justify-center leading-none text-[#172B4D]">
                <span className="text-7xl font-bold tracking-tighter tabular-nums">99</span>
                <span className="text-3xl font-bold mt-2 ml-1">€</span>
              </div>
              <p className="text-[#172B4D]/40 font-medium mt-2">{t.awards.forUpTo8Days}</p>
            </div>
            
            <div className="mt-8 w-full">
              <a href="/buchen" className="w-full py-3 rounded-lg bg-[#F4F5F7] text-[#0052CC] font-semibold text-sm hover:bg-[#0052CC] hover:text-white transition-colors duration-300 flex items-center justify-center gap-2">
                {t.awards.calculatePrice}
                <IconifyIcon icon="ph:arrow-right-bold" />
              </a>
            </div>
          </motion.div>

          {/* Card D: Insurance & Security (Col-Span-2) */}
          <motion.div 
            variants={itemVariants}
            whileHover={{ y: -4, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)" }}
            className="col-span-1 md:col-span-2 bg-white rounded-2xl p-8 md:p-10 border border-[#172B4D]/5 shadow-sm flex flex-col md:flex-row items-start md:items-center gap-8"
          >
            <div className="flex-shrink-0">
              <div className="w-20 h-20 rounded-2xl bg-[#0052CC]/5 flex items-center justify-center relative">
                <motion.div 
                  animate={{ scale: [1, 1.05, 1] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute inset-0 bg-[#0052CC]/5 rounded-2xl"
                />
                <IconifyIcon icon="ph:shield-check-fill" className="text-4xl text-[#0052CC]" />
              </div>
            </div>
            
            <div className="flex-1">
              <h3 className="text-2xl font-bold text-[#172B4D] mb-2">{t.awards.professionalValet}</h3>
              <p className="text-[#172B4D]/60 mb-6">{t.awards.vehicleSafeHands}</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  t.awards.insuredByLaw,
                  t.awards.pavedParking,
                  t.awards.ownRentedSpaces
                ].map((feature, idx) => (
                  <motion.div 
                    key={idx}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.5 + (idx * 0.1) }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-5 h-5 rounded-full bg-[#00BCC5]/10 flex items-center justify-center flex-shrink-0">
                      <IconifyIcon icon="ph:check-bold" className="text-xs text-[#00BCC5]" />
                    </div>
                    <span className="text-sm font-medium text-[#172B4D]">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

        </motion.div>
      </div>
    </section>
  );
};

export default AwardsSection2;